import React, { useEffect } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { string } from 'prop-types';
import { toast } from 'react-toastify';

const loginRegex = /^[a-z0-9.@*_-]+$/;
const agentCodeRegex = /^\d{4,12}$/;

const ModalConfirmationCopyUser = ({
  isOpen,
  toggle,
  onConfirm,
  isSubmitting = false,
  isLoadingOriginalUser = false
}) => {
  const {
    control,
    handleSubmit,
    errors,
    reset,
    clearErrors,
    trigger,
    getValues
  } = useForm({
    defaultValues: {
      name: '',
      login: '',
      password: '',
      confirmPassword: '',
      agent_code: '',
      integration_login: '',
      integration_password: ''
    },
    shouldFocusError: true
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        name: '',
        login: '',
        password: '',
        confirmPassword: '',
        agent_code: '',
        integration_login: '',
        integration_password: ''
      });
    }
  }, [isOpen, reset]);

  const onSubmit = (data) => onConfirm(data);

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    clearErrors();

    const user = getValues('login');
    const password = getValues('password');

    const message =
      'O nosso sistema identificou algumas incompatibilidades na clonação do usuário. Por favor, tente realizar o login e, caso não dê certo, informe ao suporte.';

    if (typeof user !== 'string' || user === '') {
      toast.warn(message);
      return;
    }

    if (typeof password !== 'string' || password === '') {
      toast.warn(message);
      return;
    }

    const ok = await trigger();
    if (!ok) return;
    handleSubmit(onSubmit)();
  };

  const errorText = (message) => (
    <p className="mt-1" style={{ color: 'red', fontSize: 12 }}>
      {message}
    </p>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} unmountOnClose size="md">
      <ModalHeader toggle={toggle}>Clonar Usuário</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <h5 className="mb-3">Principal</h5>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Nome*</Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Nome é obrigatório!' }}
                  defaultValue=""
                  as={
                    <Input
                      autoFocus
                      placeholder="Nome"
                      invalid={!!errors.name}
                    />
                  }
                  onChange={([e]) => {
                    clearErrors('name');
                    return e.target.value;
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => errorText(message)}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Usuário*</Label>
                <Controller
                  name="login"
                  control={control}
                  rules={{
                    required: 'Usuário é obrigatório!',
                    pattern: {
                      value: loginRegex,
                      message:
                        'Usuário inválido. Use apenas minúsculas, números e . @ * _ -'
                    }
                  }}
                  defaultValue=""
                  as={<Input placeholder="Usuário" invalid={!!errors.login} />}
                  onChange={([e]) => {
                    clearErrors('login');
                    return e.target.value;
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="login"
                  render={({ message }) => errorText(message)}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Senha*</Label>
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: 'Senha é obrigatória!' }}
                  defaultValue=""
                  as={
                    <Input
                      type="password"
                      placeholder="Digite uma senha"
                      autoComplete="new-password"
                      invalid={!!errors.password}
                    />
                  }
                  onChange={([e]) => {
                    clearErrors('password');
                    return e.target.value;
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="password"
                  render={({ message }) => errorText(message)}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Confirmar senha*</Label>
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: 'Confirmar senha é obrigatório!',
                    validate: (v) =>
                      v === getValues('password') || 'As senhas não conferem!'
                  }}
                  defaultValue=""
                  as={
                    <Input
                      type="password"
                      placeholder="Confirme a senha"
                      autoComplete="new-password"
                      invalid={!!errors.confirmPassword}
                    />
                  }
                  onChange={([e]) => {
                    clearErrors('confirmPassword');
                    return e.target.value;
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="confirmPassword"
                  render={({ message }) => errorText(message)}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Código do agente*</Label>
                <Controller
                  name="agent_code"
                  control={control}
                  rules={{
                    required: 'Código do agente é obrigatório!',
                    pattern: {
                      value: agentCodeRegex,
                      message:
                        'Código do agente deve ter entre 4 e 12 dígitos numéricos.'
                    }
                  }}
                  defaultValue=""
                  as={
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex.: 1234"
                      invalid={!!errors.agent_code}
                    />
                  }
                  onChange={([e]) => {
                    clearErrors('agent_code');
                    return e.target.value;
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="agent_code"
                  render={({ message }) => errorText(message)}
                />
              </FormGroup>
            </Col>
          </Row>

          <hr />

          <h5 className="mb-3">Parte de Integrações</h5>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Usuário</Label>
                <Controller
                  name="integration_login"
                  control={control}
                  defaultValue=""
                  as={<Input placeholder="Usuário de integração (opcional)" />}
                  onChange={([e]) => e.target.value}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Senha</Label>
                <Controller
                  name="integration_password"
                  control={control}
                  defaultValue=""
                  as={
                    <Input
                      type="password"
                      placeholder="Senha de integração (opcional)"
                    />
                  }
                  onChange={([e]) => e.target.value}
                />
              </FormGroup>
            </Col>
          </Row>

          <p className="text-muted mt-2 mb-0">
            Uma cópia será criada com o mesmo perfil, contexto, permissões e
            demais atributos do usuário original. Você só define os dados acima.
          </p>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              color="primary"
              type="button"
              onClick={handleSubmitClick}
              disabled={isSubmitting || isLoadingOriginalUser}
            >
              Confirmar
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalConfirmationCopyUser;
